import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';
import { findRecord } from '@warp-drive/utilities/json-api';
import { getItemStats, getMentionsForItem } from '../../builders/items.ts';
import type { Item } from '../../schemas/item.ts';
import type { ItemStats } from '../../schemas/item-stats.ts';
import type { Mention } from '../../schemas/mention.ts';
import type LocationService from '../../services/location';
import { hasCoordinates, type Coordinates } from '../../utils/map';
import { calculateDistance } from '../../utils/distance';

interface ItemParams {
  item_id: string;
}

interface ShowModel {
  item: Item | null;
  stats: ItemStats | null;
  mentions: Mention[];
  distance?: string | null;
  refresh: () => unknown;
}

export default class ItemsShowRoute extends Route {
  @service declare store: Store;
  @service declare location: LocationService;

  #forceRefresh = false;

  async model(params: Record<string, unknown>) {
    const { item_id } = params as unknown as ItemParams;

    const isForced = this.#forceRefresh;
    this.#forceRefresh = false;

    const itemOptions = isForced
      ? { ...findRecord('item', item_id), cacheOptions: { reload: true } }
      : findRecord('item', item_id);
    const statsOptions = isForced
      ? { ...getItemStats(item_id), cacheOptions: { reload: true } }
      : getItemStats(item_id);
    const mentionsOptions = isForced
      ? { ...getMentionsForItem(item_id), cacheOptions: { reload: true } }
      : getMentionsForItem(item_id);

    let item: Item | null = null;
    let stats: ItemStats | null = null;
    let mentions: Mention[] = [];

    try {
      const itemResult = await this.store.request<{ data: Item }>(itemOptions);
      item = itemResult.content.data;
    } catch {
      return {
        item: null,
        stats: null,
        mentions: [],
        refresh: () => this.refresh(),
      } satisfies ShowModel;
    }

    try {
      const statsResult = await this.store.request<{ data: ItemStats }>(
        statsOptions
      );
      stats = statsResult.content.data;
    } catch {
      // stats unavailable, proceed without them
    }

    try {
      const mentionsResult = await this.store.request<{ data: Mention[] }>(
        mentionsOptions
      );
      mentions = mentionsResult.content.data;
    } catch {
      // mentions unavailable, proceed without them
    }

    const model: ShowModel = {
      item,
      stats,
      mentions,
      refresh: () => {
        this.#forceRefresh = true;
        return this.refresh();
      },
    };

    // Calculate distance for places if user location available
    if (item && item.type === 'place') {
      const userLocation = this.fetchUserLocation();
      const itemLocation = this.getItemLocation(item);
      model.distance = calculateDistance(userLocation, itemLocation);
    }

    return model;
  }

  /**
   * Fetch user location using the location service.
   * Returns cached location if available, otherwise returns null
   * (we don't prompt on item pages to avoid interrupting navigation).
   */
  private fetchUserLocation(): Coordinates | null {
    // Only use cached location, don't prompt on item detail pages
    if (this.location.userLocation) {
      return this.location.userLocation;
    }
    return null;
  }

  private getItemLocation(item: Item): Coordinates | null {
    let data: unknown = item.enrichedData;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data) as unknown;
      } catch {
        return null;
      }
    }
    if (!data || typeof data !== 'object') return null;

    const rawLocation = (data as { location?: unknown }).location;
    if (!hasCoordinates(rawLocation)) return null;

    return rawLocation;
  }
}
